import { api } from "@/api/client";

// The map mirrors config/netsuite_map.yml (see the comments there for each
// field's allowed values); comments mirror NetsuiteMapCommentSerializer.

export type Direction = "W" | "RW" | "R" | "none";
export type IntegrationStatus = "live" | "gated" | "verify" | "legacy";
export type RecordRole = "creates" | "updates" | "by_hand";
export type ChainLevel = "automated" | "approved" | "by_hand" | "monitored";

export interface NsSystem {
  label: string;
  subtitle: string;
  kind: "in_house" | "partner" | "netsuite";
  writes?: string;
  feeds?: string;
  pos?: [number, number];
}

export interface NsEdge {
  from: string;
  to: string;
  kind: "netsuite" | "feeds";
}

export interface NsIntegration {
  id: string;
  name: string;
  description: string;
  owner: string;
  systems?: string[];
  cycle: string;
  direction: Direction;
  records: string[];
  trigger: string;
  since: string;
  status: IntegrationStatus;
  status_note?: string;
  note?: string;
}

export interface NsRecordSource {
  id: string;
  label: string;
  short: string;
  systems?: string[];
}

export interface NsRecordRow {
  id: string;
  name: string;
  netsuite_type: string;
  by: { source: string; role: RecordRole; note: string }[];
}

export interface NsRecordFamily {
  family: string;
  rows: NsRecordRow[];
}

export interface NsChain {
  id: string;
  title: string;
  intro: string;
  steps: { record: string; netsuite_type: string; by: { who: string; level: ChainLevel; note: string; new_2026?: boolean }[] }[];
}

export interface NsPeriod {
  label: string;
  theme: string;
  pre?: boolean;
  from?: string;
  until: string;
  current?: boolean;
  items: { text: string; highlight?: boolean }[];
}

export interface NsOpenItem {
  id: string;
  kind: "gap" | "confirm";
  title: string;
  body: string;
}

export interface NsMap {
  as_of: string;
  summary: string;
  records_note: string;
  about: string[];
  cycles: Record<string, string>;
  systems: Record<string, NsSystem>;
  edges: NsEdge[];
  integrations: NsIntegration[];
  record_sources: NsRecordSource[];
  records: NsRecordFamily[];
  chains: NsChain[];
  timeline: NsPeriod[];
  timeline_next: string[];
  open_items: NsOpenItem[];
}

export type TargetType = "integration" | "record" | "open_item" | "general";
export type CommentKind = "comment" | "suggestion";

export interface NsComment {
  id: number;
  target_type: TargetType;
  target_key: string;
  kind: CommentKind;
  body: string;
  author: string;
  author_email: string;
  created_at: string;
  resolved: boolean;
  resolved_at: string | null;
  resolved_by: string | null;
  mine: boolean;
  can_delete: boolean;
}

export interface NsMapPage {
  map: NsMap;
  comments: NsComment[];
  can_resolve: boolean;
}

export interface NewComment {
  target_type: TargetType;
  target_key: string;
  kind: CommentKind;
  body: string;
}

export async function fetchNetsuiteMap(): Promise<NsMapPage> {
  const { data } = await api.get<NsMapPage>("/netsuite_map");
  return data;
}

export async function createNetsuiteMapComment(comment: NewComment): Promise<NsComment> {
  const { data } = await api.post<NsComment>("/netsuite_map_comments", { comment });
  return data;
}

export async function deleteNetsuiteMapComment(id: number): Promise<void> {
  await api.delete(`/netsuite_map_comments/${id}`);
}

export async function setNetsuiteMapCommentResolved(id: number, resolved: boolean): Promise<NsComment> {
  const { data } = await api.post<NsComment>(`/netsuite_map_comments/${id}/${resolved ? "resolve" : "reopen"}`);
  return data;
}
